import { IGameModel } from "./IGameModel";
import { Bindable } from "bindable-data";
import { NotifyProperty } from "bindable-data";
import { DiceResult } from "./DiceResult";
import { nameof } from "bindable-data";
import { INetworkClient } from "by/gamefactory/cityversetycoon/networking/INetworkClient";
import { inject, injectable } from "@launchtray/tsyringe-async";
import { ActionsFactory } from "../flow/ActionsFactory";
import { Action } from "../flow/Action";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { DiceRollAction } from "../flow/impl/DiceRollAction";
import { GuestActionType } from "../../../networking/data/responses/GuestActionType";
import { IAnalyticProviderWrapper } from "../../../../analytics/IAnalyticProviderWrapper";
import { VirtualScreenEvent } from "../../../../analytics/events/VirtualScreenEvent";
import { GuestActionDto } from "../../../networking/data/responses/GuestActionDto";
import { SceneIndex } from "../../SceneIndex";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { ILatestNewsModel } from "../../common/models/latestnews/ILatestNewsModel";
import { NewsCurrentActionResultDto } from "../../../networking/data/responses/NewsCurrentActionResultDto";
import { NewsAction } from "../flow/impl/NewsAction";
import { BuildingRightsType } from "by/gamefactory/cityversetycoon/networking/data/responses/user/BuildingRightsType";
import { IConnectionModel } from "../../../networking/IConnectionModel";
import { ClaimCurrentActionResultDto } from "../../../networking/data/responses/ClaimCurrentActionResultDto";

@injectable()
export class GameModel extends Bindable implements IGameModel {
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    public readonly action: NotifyProperty<Action>;
    public readonly actionNumber: NotifyProperty<number>;
    public readonly diceResults: NotifyProperty<DiceResult[]>;
    private readonly networkClient: INetworkClient;
    private readonly playerModel: IPlayerModel;
    private readonly logger: ILogger;
    private readonly latestNews: ILatestNewsModel;
    private readonly connectionModel: IConnectionModel;

    public constructor(
        @inject("INetworkClient") networkClient: INetworkClient,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IAnalyticProviderWrapper") analyticsProvider: IAnalyticProviderWrapper,
        @inject("ILogger") logger: ILogger,
        @inject("ILatestNewsModel") latestNews: ILatestNewsModel,
        @inject("IConnectionModel") connectionModel: IConnectionModel
    ) {
        super();
        this.networkClient = networkClient;
        this.analyticsProvider = analyticsProvider;
        this.connectionModel = connectionModel;
        this.action = new NotifyProperty(this, nameof(this, "action"), null);
        this.actionNumber = new NotifyProperty(this, nameof(this, "actionNumber"), -1);
        this.diceResults = new NotifyProperty(this, nameof(this, "diceResults"), []);
        this.playerModel = playerModel;
        this.logger = logger;
        this.latestNews = latestNews;
    }

    public async getCurrentAction(): Promise<void> {
        if ((this.playerModel.buildingRights.value === BuildingRightsType.OWNER && this.playerModel.buildings.value.length === 0) || !this.connectionModel.connected.value) {
            return;
        }
        const result = await this.networkClient.getCurrentAction();
        this.action.setValue(ActionsFactory.createFromDto(result.data.currentAction));
        this.actionNumber.setValue(result.data.currentAction.rawActionNumber);
        this.registerAnalyticEvent(result.data.currentAction);
        this.latestNews.getLatestNewsStatus();
    }

    public async claimAction(): Promise<NewsCurrentActionResultDto> {
        let claimActionResultDto: ClaimCurrentActionResultDto;
        try {
            const result = await this.networkClient.claimAction();
            claimActionResultDto = result.data;
        } catch {
            claimActionResultDto = {
                guest: undefined,
                nextAction: undefined,
                currentAction: undefined
            };
        }
        if (claimActionResultDto.guest != undefined && this.action.value != this.action.value as NewsAction) {
            this.playerModel.updateValuesFromClaim(claimActionResultDto.guest);
        }
        if (claimActionResultDto.nextAction != undefined) {
            this.action.setValue(ActionsFactory.createFromDto(claimActionResultDto.nextAction));
            this.actionNumber.setValue(claimActionResultDto.nextAction.rawActionNumber);
            this.registerAnalyticEvent(claimActionResultDto.nextAction);
        } else {
            this.action.setValue(new DiceRollAction({
                type: GuestActionType.DICE_ROLL,
                rawActionNumber: 0,
                diceRollCombination: ["JAIL", "STEAL", "VISIT"],
                diceRollAction: "DEAD_ROLL"
            }));
            this.actionNumber.setValue(0);
            this.playerModel.loadBuildings();
            this.logger.warn("Next action not defined. We should handle this situation.");
        }
        this.latestNews.getLatestNewsStatus();
        return claimActionResultDto.currentAction;
    }

    private registerAnalyticEvent(dto: GuestActionDto): void {
        switch (dto.type) {
            case GuestActionType.DIALOG:
                if (dto.dialogType == SceneIndex.ADD_TO_HOME_SCREEN) {
                    return;
                }
                this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent(`${dto.dialogType}`, dto.rawActionNumber));
                break;
            case GuestActionType.DICE_ROLL:
                this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent(`${dto.diceRollAction}`, dto.rawActionNumber));
                break;
            case GuestActionType.SCREEN:
                this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent(`${dto.screenType}`, dto.rawActionNumber));
                break;
            case GuestActionType.NEWS:
                this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent(`${dto.news.type}`, dto.rawActionNumber));
                break;
        }
    }
}
